import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { AlertTriangle } from 'lucide-react';
import KnownIssuesList from './KnownIssuesList';
import CreateIssueForm from './CreateIssueForm';
import EditIssueForm from './EditIssueForm';
import IssueDetails from './IssueDetails';
import { useToast } from '../hooks/use-toast';


const API = "/api";

const KnownIssuesManager = ({ productId, currentUser }) => {
  // Safety check - don't render if productId is not provided
  if (!productId) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <AlertTriangle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">Product ID is required to display known issues</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingIssue, setEditingIssue] = useState(null);
  const [viewingIssue, setViewingIssue] = useState(null);
  const { toast } = useToast();

  // Fetch issues for this product
  const fetchIssues = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/issues/product/${productId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        setIssues(result.data || []);
      } else {
        throw new Error(result.message || 'Failed to fetch issues');
      }
    } catch (error) {
      console.error('Error fetching issues:', error);
      toast({
        title: "Error",
        description: "Failed to load known issues",
        variant: "destructive",
      });
      setIssues([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (productId) {
      fetchIssues();
    }
  }, [productId]);

  const handleCreateIssue = async (issueData) => {
    try {
      // The API call is handled in CreateIssueForm, just refresh the list
      await fetchIssues();
      setShowCreateForm(false);
    } catch (error) {
      console.error('Error creating issue:', error);
    }
  };

  const handleUpdateIssue = async (updatedIssue) => {
    try {
      await fetchIssues();
      setEditingIssue(null);
    } catch (error) {
      console.error('Error updating issue:', error);
    }
  };

  const handleCloseIssue = async (issue) => {
    try {
      const response = await fetch(`${API}/issues/${issue.id}/close`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast({
          title: "Success",
          description: "Issue closed successfully",
        });
        await fetchIssues();
      } else {
        throw new Error(result.message || 'Failed to close issue');
      }
    } catch (error) {
      console.error('Error closing issue:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to close issue",
        variant: "destructive",
      });
    }
  };

  const handleReopenIssue = async (issue) => {
    try {
      const response = await fetch(`${API}/issues/${issue.id}/reopen`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast({
          title: "Success",
          description: "Issue reopened successfully",
        });
        await fetchIssues();
      } else {
        throw new Error(result.message || 'Failed to reopen issue');
      }
    } catch (error) {
      console.error('Error reopening issue:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to reopen issue",
        variant: "destructive",
      });
    }
  };

  const handleEditIssue = (issue) => {
    setEditingIssue(issue);
  };

  const handleIssueClick = (issue) => {
    setViewingIssue(issue);
  };

  return (
    <>
      <KnownIssuesList
        issues={issues}
        loading={loading}
        currentUser={currentUser}
        onCreateIssue={() => setShowCreateForm(true)}
        onEditIssue={handleEditIssue}
        onCloseIssue={handleCloseIssue}
        onReopenIssue={handleReopenIssue}
        onIssueClick={handleIssueClick}
      />

      {/* Create Issue Form Modal */}
      {showCreateForm && (
        <CreateIssueForm
          productId={productId}
          currentUser={currentUser}
          onSubmit={handleCreateIssue}
          onCancel={() => setShowCreateForm(false)}
          isOpen={showCreateForm}
          onClose={() => setShowCreateForm(false)}
        />
      )}

      {/* Issue Details Modal */}
      {viewingIssue && (
        <IssueDetails
          issue={viewingIssue}
          onClose={() => setViewingIssue(null)}
          onEdit={() => {
            setViewingIssue(null);
            setEditingIssue(viewingIssue);
          }}
          onCloseIssue={handleCloseIssue}
          onReopenIssue={handleReopenIssue}
          currentUser={currentUser}
        />
      )}

      {/* Edit Issue Form Modal */}
      {editingIssue && (
        <EditIssueForm
          issue={editingIssue}
          onUpdate={handleUpdateIssue}
          onClose={() => setEditingIssue(null)}
        />
      )}
    </>
  );
};

export default KnownIssuesManager;